import React, { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { legalAPI } from '../services/api';
import { FileText, AlertCircle, CheckCircle } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import ReactMarkdown from 'react-markdown';
import { useAuth } from '../context/AuthContext';

const TermsAndConditionsPage: React.FC = () => {
  const { t, i18n } = useTranslation();
  const { user } = useAuth();
  const [searchParams] = useSearchParams();
  // Determine userType from URL param, user role, or default to customer
  const urlUserType = searchParams.get('userType');
  const userType = (urlUserType || (user?.role === 'provider' ? 'driver' : 'customer')) as 'driver' | 'customer';
  const [content, setContent] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    fetchLegalDocument();
  }, [userType, i18n.language]);

  const fetchLegalDocument = async () => {
    try {
      setLoading(true);
      const language = i18n.language || 'en';
      console.log(`Fetching terms for userType: ${userType}, language: ${language}`);
      const response = await legalAPI.getLegalDocument('terms', userType, language);
      if (response.success && response.data) {
        console.log(`Terms fetched successfully. Content length: ${response.data.content?.length || 0}`);
        setContent(response.data.content);
      } else {
        setContent(null);
      }
    } catch (err: any) {
      console.error('Error fetching terms:', err);
      setError(err.message);
      setContent(null);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-white">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        {/* Header */}
        <div className="bg-white rounded-lg shadow-sm p-8 mb-8">
          <div className="flex items-center mb-4">
            <FileText className="h-8 w-8 text-black mr-3" />
            <h1 className="text-3xl font-bold text-black">
              {t('legal.termsAndConditions')} {userType === 'driver' ? '(Driver)' : '(Customer)'}
            </h1>
          </div>
          <p className="text-gray-500">{t('legal.lastUpdated')}: {new Date().toLocaleDateString()}</p>
        </div>

        {/* Important Notice */}
        <div className="bg-yellow-50 border-l-4 border-yellow-400 p-6 mb-8 rounded-lg">
          <div className="flex items-start">
            <AlertCircle className="h-6 w-6 text-yellow-600 mr-3 mt-0.5 flex-shrink-0" />
            <div>
              <h2 className="text-lg font-semibold text-yellow-900 mb-2">{t('legal.termsImportantNotice')}</h2>
              <p className="text-yellow-800 text-sm leading-relaxed">
                {t('legal.termsImportantText')}
              </p>
            </div>
          </div>
        </div>

        {/* Content */}
        {loading ? (
          <div className="bg-white rounded-lg shadow-sm p-8 text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-black mx-auto"></div>
            <p className="mt-4 text-gray-500">{t('legal.loadingTerms')}</p>
          </div>
        ) : error && !content ? (
          <div className="bg-red-50 border border-red-200 rounded-lg p-8 text-center">
            <AlertCircle className="h-12 w-12 text-red-600 mx-auto mb-4" />
            <p className="text-red-800 font-medium">{t('legal.failedToLoad')} {t('legal.termsAndConditions').toLowerCase()}</p>
            <p className="text-red-600 text-sm mt-2">{error}</p>
          </div>
        ) : content ? (
          <div className="bg-white rounded-lg shadow-sm p-8">
            <div className="markdown-content">
              <ReactMarkdown
                components={{
                  h1: ({node, ...props}) => <h1 className="text-3xl font-bold text-black mt-8 mb-4 pb-2 border-b border-gray-200" {...props} />,
                  h2: ({node, ...props}) => <h2 className="text-2xl font-bold text-black mt-6 mb-3" {...props} />,
                  h3: ({node, ...props}) => <h3 className="text-xl font-semibold text-black mt-4 mb-2" {...props} />,
                  p: ({node, ...props}) => <p className="text-gray-700 leading-relaxed mb-4" {...props} />,
                  ul: ({node, ...props}) => <ul className="list-disc list-inside text-gray-700 my-4 space-y-2 ml-4" {...props} />,
                  ol: ({node, ...props}) => <ol className="list-decimal list-inside text-gray-700 my-4 space-y-2 ml-4" {...props} />,
                  li: ({node, ...props}) => <li className="text-gray-700 mb-2" {...props} />,
                  strong: ({node, ...props}) => <strong className="font-semibold text-black" {...props} />,
                  em: ({node, ...props}) => <em className="italic text-gray-700" {...props} />,
                  code: ({node, ...props}) => <code className="bg-gray-100 text-gray-800 px-1 py-0.5 rounded text-sm" {...props} />,
                  blockquote: ({node, ...props}) => <blockquote className="border-l-4 border-gray-300 pl-4 italic text-gray-600 my-4" {...props} />,
                  a: ({node, ...props}) => <a className="text-black underline hover:text-gray-600" {...props} />,
                }}
              >
                {content}
              </ReactMarkdown>
            </div>
          </div>
        ) : (
          <div className="bg-white rounded-lg shadow-sm p-8 space-y-8">
          {/* Section 1 */}
          <section>
            <h2 className="text-2xl font-bold text-white mb-4">1. Platform Overview</h2>
            <div className="prose prose-gray max-w-none">
              <p className="text-gray-700 leading-relaxed mb-4">
                AllAvail operates as an <strong>intermediary platform</strong> that connects customers seeking services 
                with independent service providers. We facilitate the booking process and provide a marketplace for service discovery.
              </p>
              <div className="bg-blue-50 border-l-4 border-blue-400 p-4 my-4">
                <p className="text-blue-800 text-sm font-medium">
                  <strong>Key Point:</strong> AllAvail acts solely as a facilitator and does not provide the services directly.
                </p>
              </div>
            </div>
          </section>

          {/* Section 2 */}
          <section>
            <h2 className="text-2xl font-bold text-white mb-4">2. Service Provider Relationship</h2>
            <div className="prose prose-gray max-w-none">
              <p className="text-gray-700 leading-relaxed mb-4">
                AllAvail explicitly states and confirms that:
              </p>
              <ul className="space-y-3 ml-6">
                <li className="flex items-start text-gray-700">
                  <CheckCircle className="h-5 w-5 text-black mr-2 mt-0.5 flex-shrink-0" />
                  <span>
                    <strong>Independent Contractors:</strong> All service providers on our platform are independent contractors 
                    and are NOT employees, agents, or representatives of AllAvail.
                  </span>
                </li>
                <li className="flex items-start text-gray-700">
                  <CheckCircle className="h-5 w-5 text-black mr-2 mt-0.5 flex-shrink-0" />
                  <span>
                    <strong>No Employment Relationship:</strong> There exists no employer-employee relationship between 
                    AllAvail and any service provider listed on the platform.
                  </span>
                </li>
                <li className="flex items-start text-gray-700">
                  <CheckCircle className="h-5 w-5 text-black mr-2 mt-0.5 flex-shrink-0" />
                  <span>
                    <strong>Provider Autonomy:</strong> Service providers maintain complete control over their schedules, 
                    pricing (within platform guidelines), and service delivery methods.
                  </span>
                </li>
              </ul>
            </div>
          </section>

          {/* Section 3 */}
          <section>
            <h2 className="text-2xl font-bold text-white mb-4">3. Verification and Background Checks</h2>
            <div className="bg-red-50 border-l-4 border-red-500 p-6 rounded-lg">
              <h3 className="text-lg font-semibold text-red-900 mb-3 flex items-center">
                <AlertCircle className="h-5 w-5 mr-2" />
                Critical Disclaimer
              </h3>
              <div className="space-y-3 text-red-800">
                <p className="font-medium">
                  AllAvail does NOT conduct police background verification, comprehensive background checks, 
                  or criminal record verification of service providers.
                </p>
                <p className="text-sm">
                  We do not claim, represent, warrant, or guarantee that any service provider on our platform has:
                </p>
                <ul className="list-disc list-inside ml-4 text-sm space-y-1">
                  <li>A clean criminal record</li>
                  <li>Police clearance certificates</li>
                  <li>Undergone comprehensive background verification</li>
                  <li>Been vetted by law enforcement agencies</li>
                </ul>
                <p className="text-sm font-medium mt-4">
                  Users acknowledge and accept that they engage service providers at their own risk and discretion.
                </p>
              </div>
            </div>
          </section>

          {/* Section 4 */}
          <section>
            <h2 className="text-2xl font-bold text-white mb-4">4. Limitation of Liability</h2>
            <div className="prose prose-gray max-w-none">
              <p className="text-gray-700 leading-relaxed mb-4">
                AllAvail shall not be held liable for:
              </p>
              <ul className="space-y-2 ml-6 text-gray-700">
                <li className="flex items-start">
                  <span className="mr-2">•</span>
                  <span>Quality, timeliness, or completion of services provided by independent contractors</span>
                </li>
                <li className="flex items-start">
                  <span className="mr-2">•</span>
                  <span>Any disputes, damages, injuries, or losses arising from service provision</span>
                </li>
                <li className="flex items-start">
                  <span className="mr-2">•</span>
                  <span>Acts, omissions, negligence, or misconduct of service providers</span>
                </li>
                <li className="flex items-start">
                  <span className="mr-2">•</span>
                  <span>Property damage, personal injury, or any consequential damages</span>
                </li>
                <li className="flex items-start">
                  <span className="mr-2">•</span>
                  <span>Criminal activities or fraudulent behavior by service providers</span>
                </li>
              </ul>
              <p className="text-gray-700 leading-relaxed mt-4">
                Our liability is limited strictly to the platform facilitation services we provide, and we expressly 
                disclaim any responsibility for the actual service delivery.
              </p>
            </div>
          </section>

          {/* Section 5 */}
          <section>
            <h2 className="text-2xl font-bold text-white mb-4">5. User Responsibilities</h2>
            <div className="prose prose-gray max-w-none">
              <p className="text-gray-700 leading-relaxed mb-4">
                As a user of AllAvail, you acknowledge and agree to:
              </p>
              <ul className="space-y-2 ml-6 text-gray-700">
                <li className="flex items-start">
                  <span className="mr-2">•</span>
                  <span>Exercise due diligence when selecting service providers</span>
                </li>
                <li className="flex items-start">
                  <span className="mr-2">•</span>
                  <span>Read reviews and ratings before making booking decisions</span>
                </li>
                <li className="flex items-start">
                  <span className="mr-2">•</span>
                  <span>Report any suspicious behavior or safety concerns immediately</span>
                </li>
                <li className="flex items-start">
                  <span className="mr-2">•</span>
                  <span>Verify the identity and credentials of service providers independently</span>
                </li>
                <li className="flex items-start">
                  <span className="mr-2">•</span>
                  <span>Take necessary precautions to ensure personal safety during service interactions</span>
                </li>
              </ul>
            </div>
          </section>

          {/* Section 6 */}
          <section>
            <h2 className="text-2xl font-bold text-white mb-4">6. Payment and Refunds</h2>
            <div className="prose prose-gray max-w-none">
              <p className="text-gray-700 leading-relaxed mb-4">
                AllAvail processes payments on behalf of service providers. Our platform fee covers technology, 
                maintenance, and facilitation costs. Refund policies are subject to individual service provider terms 
                and platform guidelines.
              </p>
            </div>
          </section>

          {/* Section 7 */}
          <section>
            <h2 className="text-2xl font-bold text-white mb-4">7. Dispute Resolution</h2>
            <div className="prose prose-gray max-w-none">
              <p className="text-gray-700 leading-relaxed mb-4">
                While we provide a complaint management system to facilitate resolution, AllAvail acts only as a 
                mediator. Final dispute resolution responsibility lies between the customer and the service provider. 
                We encourage amicable settlements but do not guarantee specific outcomes.
              </p>
            </div>
          </section>

          {/* Section 8 */}
          <section>
            <h2 className="text-2xl font-bold text-white mb-4">8. Modifications to Terms</h2>
            <div className="prose prose-gray max-w-none">
              <p className="text-gray-700 leading-relaxed">
                AllAvail reserves the right to modify these terms and conditions at any time. Continued use of the 
                platform after changes constitute acceptance of the modified terms. Users will be notified of material 
                changes via email or platform notifications.
              </p>
            </div>
          </section>

          {/* Section 9 */}
          <section>
            <h2 className="text-2xl font-bold text-white mb-4">9. Contact Information</h2>
            <div className="bg-white rounded-lg p-6">
              <p className="text-gray-700 leading-relaxed">
                If you have questions about these terms and conditions, please reach out via <strong>User &gt; Raise an Issue</strong>
              </p>
            </div>
          </section>

          {/* Acceptance */}
          <section className="border-t-2 border-gray-200 pt-6">
            <div className="bg-white rounded-lg p-6">
              <h3 className="text-lg font-semibold text-black mb-3">Acceptance of Terms</h3>
              <p className="text-gray-800 text-sm leading-relaxed">
                By using AllAvail, you acknowledge that you have read, understood, and agree to be bound by these 
                terms and conditions. If you do not agree with any part of these terms, you must discontinue use of 
                the platform immediately.
              </p>
            </div>
          </section>
        </div>
        )}
      </div>
    </div>
  );
};

export default TermsAndConditionsPage;

